import { type StateCreator } from "zustand";

import { type TDensity } from "@/components/data-grid/types";

const STORAGE_KEY = "fundy:data-grid-prefs";

const isBrowser = () => typeof window !== "undefined" && typeof localStorage !== "undefined";

/** Per-grid persisted preferences, keyed by the grid's `persistKey`. */
export type TGridPreferences = {
  /** Column id → visible. Absent columns default to visible. */
  columnVisibility: Record<string, boolean>;
  /** Ordered column ids (empty = use declaration order). */
  columnOrder: string[];
  density: TDensity;
  pageSize: number;
};

export type TDataGridStoreState = {
  /** persistKey → preferences. */
  gridPreferences: Record<string, TGridPreferences>;
  /** True once the slice has been hydrated from localStorage. */
  isDataGridHydrated: boolean;
};

export type TDataGridStoreActions = {
  hydrateDataGridPrefs: () => void;
  getGridPreferences: (persistKey: string) => TGridPreferences | undefined;
  setColumnVisibility: (persistKey: string, columnVisibility: Record<string, boolean>) => void;
  setColumnOrder: (persistKey: string, columnOrder: string[]) => void;
  setDensity: (persistKey: string, density: TDensity) => void;
  setGridPageSize: (persistKey: string, pageSize: number) => void;
  resetGridPreferences: (persistKey: string) => void;
};

export type TDataGridStore = TDataGridStoreState & TDataGridStoreActions;

const initialState: TDataGridStoreState = {
  gridPreferences: {},
  isDataGridHydrated: false,
};

function readFromStorage(): Record<string, TGridPreferences> {
  if (!isBrowser()) return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, TGridPreferences>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeToStorage(prefs: Record<string, TGridPreferences>): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore quota / serialization errors — prefs are best-effort.
  }
}

export const createDataGridSlice: StateCreator<TDataGridStore> = (set, get) => {
  const mutate = (persistKey: string, patch: Partial<TGridPreferences>) => {
    const current = get().gridPreferences;
    const existing: TGridPreferences =
      current[persistKey] ?? { columnVisibility: {}, columnOrder: [], density: "comfortable", pageSize: 20 };
    const next = { ...current, [persistKey]: { ...existing, ...patch } };
    set({ gridPreferences: next });
    writeToStorage(next);
  };

  return {
    ...initialState,

    hydrateDataGridPrefs: () => {
      if (get().isDataGridHydrated) return;
      set({ gridPreferences: readFromStorage(), isDataGridHydrated: true });
    },

    getGridPreferences: (persistKey) => get().gridPreferences[persistKey],

    setColumnVisibility: (persistKey, columnVisibility) => mutate(persistKey, { columnVisibility }),

    setColumnOrder: (persistKey, columnOrder) => mutate(persistKey, { columnOrder }),

    setDensity: (persistKey, density) => mutate(persistKey, { density }),

    setGridPageSize: (persistKey, pageSize) => mutate(persistKey, { pageSize }),

    resetGridPreferences: (persistKey) => {
      const current = { ...get().gridPreferences };
      delete current[persistKey];
      set({ gridPreferences: current });
      writeToStorage(current);
    },
  };
};
